import { displayName } from '../../language-names';

const s = (n: number | string) => (String(n) === '1' ? '' : 's');
const orList = (words: readonly string[]) => (words.length < 2 ? words.join('') : `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`);
const andList = (words: readonly string[]) => (words.length < 2 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`);

/**
 * What changed in a field's own settings, for Publish, after its name:
 * "“Bio”: takes at most 200 characters", "“Visit”: not on Saturday or Sunday".
 */
export const settingsChanges = {
  line: (name: string, words: string) => `“${name}”: ${words}`,
  anySize: 'takes any number of characters',
  size: (n: number) => `takes at most ${n} characters`,
  fromTo: (min: number, max: number) => `from ${min} to ${max}`,
  atLeast: (min: number) => `at least ${min}`,
  atMost: (max: number) => `at most ${max}`,
  anyNumber: 'any number',
  between: (a: string, b: string) => `between ${a} and ${b}`,
  noEarlier: (day: string) => `no earlier than ${day}`,
  noLater: (day: string) => `no later than ${day}`,
  anyDay: 'any day',
  today: 'today',
  daysAfter: (n: number) => `${n} day${s(n)} after today`,
  daysBefore: (n: number) => `${n} day${s(n)} before today`,
  usualDecimals: 'the usual decimals',
  decimals: (n: number) => `${n} decimal${s(n)}`,
  /** How an amount is written where it is read, by an example: "written as 1,610,000 EGP". */
  writtenAs: (example: string) => `written as ${example}`,
  writtenAsUsual: 'written as usual',
  /** The days of the week, Monday first. */
  dayNames: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as readonly string[],
  notOn: (days: readonly string[]) => `not on ${orList(days)}`,
  anyWeekday: 'any day of the week',
  startsToday: 'starts on today',
  startsEmpty: 'starts empty',
  rows: (n: number) => `starts ${n} rows high`,
  keepsHeight: 'keeps its height as people type',
  grows: 'grows as people type',
  before: (unit: string) => `shows “${unit}” before the number`,
  noBefore: 'no unit before the number',
  after: (unit: string) => `shows “${unit}” after the number`,
  noAfter: 'no unit after the number',
  atStart: (words: string) => `“${words}” at the start`,
  noStart: 'no words at the start',
  atEnd: (words: string) => `“${words}” at the end`,
  noEnd: 'no words at the end',
  nps: 'coloured as NPS',
  noNps: 'no longer coloured as NPS',
  icons: { heart: 'shown as hearts', thumb: 'shown as thumbs up', number: 'shown as numbers', star: 'shown as stars' },
  timeFrom: (time: string) => `no earlier than ${time}`,
  anyTimeFrom: 'any time from midnight',
  timeUntil: (time: string) => `no later than ${time}`,
  anyTimeUntil: 'any time until midnight',
  takesAtMost: (n: number) => `takes at most ${n}`,
  takesAny: 'takes any number',
  colours: { success: 'always green', warning: 'always amber', danger: 'always red', info: 'always blue', none: 'coloured by how far it has come' },
  noPercent: 'no percent on the bar',
  percent: 'the percent on the bar',
  apart: (separator: string) => `keywords apart by “${separator}”`,
  suggests: (words: readonly string[]) => `suggests ${andList(words)}`,
  suggestsNothing: 'suggests nothing',
  everyMinutes: (n: number) => `every ${n} minutes`,
  anyMinute: 'any minute',
  stepsBy: (n: number) => `steps by ${n}`,
  // ---- the business kinds' own
  endsOn: (field: string) => `ends on “${field}”`,
  oneDate: 'one date, with no end',
  runsFrom: (field: string) => `runs from “${field}”`,
  onZone: (zone: string) => `on the clock of ${zone}`,
  // ---- a text typed in several languages, each by a person (its languages by their names)
  languageName: (tag: string) => displayName('en', tag),
  typedIn: (languages: readonly string[]) => `typed in ${andList(languages)}`,
  oneLanguage: 'typed in one language',
  requiredIn: (languages: readonly string[]) => `a required answer typed in ${andList(languages)}`,
  onPageZone: 'on the page’s clock',
  scheduleOf: (name: string) => `asks for the free times of “${name}”`,
  ownSchedule: 'asks for the free times by its own name',
  lasts: (n: number) => `a booking lasts ${n} minutes`,
  lastsAsSaid: 'a booking lasts as long as the app says',
  keptFree: (n: number) => `${n} minutes kept free after each booking`,
  noneKeptFree: 'no minutes kept free between bookings',
  endsIn: (field: string) => `its end goes in “${field}”`,
  noEndWritten: 'no end written',
  askedAgain: (fields: readonly string[]) => `asked again when ${andList(fields.map((f) => `“${f}”`))} changes`,
  askedOnce: 'asked again only for another month',
  runsFromNothing: 'shows only the time logged',
  keptInMinutes: 'kept in minutes',
  keptInHours: 'kept in hours',
  wordsBeside: 'its words beside the dot',
  noWordsBeside: 'the dot alone',
  height: (px: number) => `${px} pixels high`,
  usualHeight: '480 pixels high',
  qrLevelOf: (level: string) => `error correction ${({ L: 'low', M: 'medium', Q: 'quartile', H: 'high' } as Record<string, string>)[level] ?? level}`,
  codeSize: (px: number) => `its code ${px} pixels`,
  usualCodeSize: 'its code as large as usual',
  codeWordsUnder: 'its words under its code',
  codeAlone: 'its code alone',
  pinsOnPicture: (address: string) => `its pins on the picture at ${address}`,
  pinsOnPictureOf: (field: string) => `its pins on the picture in “${field}”`,
  pinsNoPicture: 'its pins on no picture',
  pictureShows: (words: string) => `its picture said as “${words}”`,
  pictureUnsaid: 'its picture not said in words',
  pinsAtMost: (n: number) => `at most ${n} pins`,
  pinsAny: 'any number of pins',
  accountsFrom: (model: string) => `accounts from ${model}`,
  currencyFrom: (field: string) => `in the currency of “${field}”`,
  noCurrency: 'in no currency',
  taxTyped: 'its tax amounts can be typed',
  taxShown: 'its tax amounts are only shown',
  amountDueFrom: (field: string) => `the amount due from “${field}”`,
  noAmountDue: 'no amount due under it',
  propertyColumns: (n: number): string => (n === 2 ? 'its properties in two columns' : 'its properties in one column'),
  canAddProperty: 'people may add a property',
  cannotAddProperty: 'people may not add a property',
  // ---- scanned with the camera
  scanned: 'scanned with the camera',
  scannedBy: (field: string) => `scanned with the camera, a record found by its “${field}”`,
  notScanned: 'no longer scanned with the camera',
  // ---- code
  writtenIn: (language: string) => `written in ${language}`,
  plainText: 'plain text',
};
